import { Copy, ImageOff, Images, Layers } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shared/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import { Label } from '@/shared/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group'
import { Skeleton } from '@/shared/ui/skeleton'
import { Spinner } from '@/shared/ui/spinner'
import {
  useCopyGalleryMutation,
  useDocumentThumbnailQuery,
  useGallerySourcesQuery
} from '../hooks/use-owner-documents'
import { CATALOG_OWNER_LABEL, type CatalogOwnerType } from '../models/document-owner'
import type { CopyGalleryMode, GallerySourceRow } from '../services/documents-admin-v2.service'

/**
 * «COPIAR DESDE…»: traer la galería de otro dueño sin volver a subir los archivos.
 *
 * ── QUÉ PROBLEMA RESUELVE ───────────────────────────────────────────────────
 *
 * Varios subtipos comparten el mismo render de carrusel, y hasta ahora la única forma de tenerlo
 * en los dos era subir el JPG dos veces. Detrás de este diálogo, la API crea filas nuevas sobre el
 * MISMO asset y el mismo path (ADR 0022): no se mueve un byte.
 *
 * ── DOS GRUPOS, Y LA DISTINCIÓN ES LA MITAD DE LA PANTALLA ──────────────────
 *
 * - **Otros subtipos del proyecto** (`sibling`). Es el caso que pidió el negocio, y va primero
 *   porque es el que tiene fotos: el proyecto muchas veces no tiene ninguna.
 * - **De la cadena** (`chain`): el subtipo, el proyecto, la sociedad. Copiar de acá **materializa
 *   lo heredado** — el destino pasa a tener como propias unas fotos que ya veía, para poder
 *   reordenarlas o quitar alguna sin tocar el origen, que le sirve a los otros 102 inmuebles.
 *
 * Esa segunda frase es la que evita el malentendido caro: alguien que copia del proyecto «para
 * que se vean» cuando ya se veían, y termina con el doble de fotos.
 *
 * ── EL ALCANCE SE VE ANTES DE ACTUAR ────────────────────────────────────────
 *
 * Cada candidato muestra su miniatura de portada y cuántas fotos tiene, y el modo `replace` dice
 * explícitamente qué hace con lo propio del destino —sacarlo del slot, no borrarlo—. Elegir a
 * ciegas entre «sumar» y «reemplazar» sobre una galería que otro acomodó es exactamente el gesto
 * que no se puede deshacer con un botón.
 */
type GalleryCopyFromDialogProps = {
  ownerType: CatalogOwnerType
  ownerId: string
  /** Cuántas fotos tiene YA el destino. Decide si `replace` tiene sentido ofrecerlo. */
  currentPhotoCount: number
  className?: string
}

/** Cómo se titula cada grupo. En tuteo neutro y en caja de oración, como el resto del cartapacio. */
const GROUP_LABEL: Record<GallerySourceRow['relation'], string> = {
  sibling: 'Otros subtipos de este proyecto',
  chain: 'De la cadena de este nivel'
}

/**
 * Una fila del diálogo: miniatura, rótulo y cuántas fotos tiene.
 *
 * Se pide la HOJA GENERADA (`useDocumentThumbnailQuery`) y no el original: es el mismo hook que
 * usa la galería, así que un candidato ya visto sale de caché sin un viaje, y evita traerse una
 * foto de 35 megapíxeles para pintarla en 48 px. Sin fotos no se pide nada — el `enabled` corta
 * la consulta antes de salir— y una hoja ausente (`url: null`) cae al ícono, que es la verdad.
 */
function SourceOption({ source }: { source: GallerySourceRow }) {
  const empty = source.photoCount === 0
  const preview = useDocumentThumbnailQuery(source.coverDocumentId ?? '', !empty)

  return (
    <Label
      htmlFor={`gallery-source-${source.ownerId}`}
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-2 transition-colors',
        'has-[button[data-state=checked]]:border-primary/40 has-[button[data-state=checked]]:bg-accent/40',
        empty ? 'cursor-not-allowed opacity-55' : 'hover:bg-accent/30'
      )}
    >
      <RadioGroupItem
        id={`gallery-source-${source.ownerId}`}
        value={`${source.ownerType}:${source.ownerId}`}
        // Un candidato sin fotos se muestra igual, deshabilitado: sacarlo de la lista dejaría a
        // quien busca el proyecto creyendo que el diálogo está roto.
        disabled={empty}
      />

      <span className="flex h-9 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[3px] border bg-muted/50">
        {empty ? (
          <ImageOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        ) : preview.isLoading ? (
          <Skeleton className="h-full w-full rounded-none" />
        ) : preview.data?.urls?.[0] ? (
          // `alt` vacío A PROPÓSITO: el rótulo de al lado ya nombra el dueño, y el nombre del
          // archivo no describe la foto.
          <img
            // La PRIMERA hoja: es la portada del documento, y acá se dibuja una miniatura de 48 px.
            src={preview.data.urls[0]}
            alt=""
            className="h-full w-full object-cover"
            decoding="async"
            loading="lazy"
          />
        ) : (
          <ImageOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        )}
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium text-xs">{source.label}</span>
        <span className="text-[11px] text-muted-foreground">
          {empty
            ? 'Sin fotos en su galería'
            : source.photoCount === 1
              ? '1 foto'
              : `${source.photoCount} fotos`}
        </span>
      </span>
    </Label>
  )
}

export function GalleryCopyFromDialog({
  ownerType,
  ownerId,
  currentPhotoCount,
  className
}: GalleryCopyFromDialogProps) {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [mode, setMode] = useState<CopyGalleryMode>('append')
  const modeName = useId()

  // La lista se pide al ABRIR y no con la pestaña: son los hermanos de un proyecto con sus
  // conteos, y casi nadie abre esto en una visita dada.
  const sources = useGallerySourcesQuery(ownerType, ownerId, open)
  const copy = useCopyGalleryMutation(ownerType, ownerId)

  // Cada apertura arranca limpia. Sin esto, cerrar sin confirmar y volver a abrir dejaría
  // preseleccionado un origen que la persona ya había descartado — y `replace` armado.
  useEffect(() => {
    if (!open) return
    setSelected(null)
    setMode('append')
  }, [open])

  const candidates = sources.data ?? []
  const usable = candidates.some((candidate) => candidate.photoCount > 0)

  /*
    ☠️ LO QUE TIENE FOTOS VA PRIMERO — dentro de cada grupo y entre los grupos.

    Un proyecto de VARENA tiene VEINTE subtipos hermanos, y lo normal hoy es que ninguno tenga
    galería todavía: el único candidato usable es el proyecto. Con el orden fijo `sibling` →
    `chain`, la lista abría con veinte filas deshabilitadas y había que scrollear las veinte para
    encontrar la única que sirve. Se ve en la primera captura de la tajada.

    No se ESCONDE lo vacío: un candidato en cero le dice a quien lo busca que existe y que está
    vacío, que es distinto de no aparecer. Sólo se ordena.
  */
  const byPhotos = (left: GallerySourceRow, right: GallerySourceRow) =>
    right.photoCount - left.photoCount
  const groups = (['sibling', 'chain'] as const)
    .map((relation) => ({
      relation,
      rows: candidates.filter((candidate) => candidate.relation === relation).sort(byPhotos)
    }))
    .filter((group) => group.rows.length > 0)
    .sort((left, right) => (right.rows[0]?.photoCount ?? 0) - (left.rows[0]?.photoCount ?? 0))

  const handleCopy = () => {
    if (!selected) return
    const [fromType, fromId] = selected.split(':') as [CatalogOwnerType, string]
    copy.mutate(
      { from: { ownerType: fromType, ownerId: fromId }, mode },
      {
        onSuccess: () => {
          setOpen(false)
        }
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn('shrink-0 gap-1.5', className)}
        >
          <Copy className="h-3.5 w-3.5" />
          Copiar desde…
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Copiar una galería</DialogTitle>
          <DialogDescription>
            {/* «Copia … en {label}» y no «trae … a {label}»: los rótulos del catálogo llevan
                artículo («el subtipo», «la sociedad») y «a el subtipo» es lo que salía. */}
            Copia las fotos de otro nivel en {CATALOG_OWNER_LABEL[ownerType]} sin volver a subirlas.
            Los archivos no se duplican: se reusan los mismos.
          </DialogDescription>
        </DialogHeader>

        {sources.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 rounded-md" />
            <Skeleton className="h-12 rounded-md" />
            <Skeleton className="h-12 rounded-md" />
          </div>
        ) : sources.isError ? (
          <QueryErrorState
            error={sources.error}
            resource="los orígenes de la galería"
            onRetry={() => {
              void sources.refetch()
            }}
          />
        ) : !usable ? (
          <Empty className="py-6">
            <EmptyHeader>
              <EmptyMedia>
                <Images className="h-6 w-6 text-muted-foreground" aria-hidden />
              </EmptyMedia>
              <EmptyTitle>No hay de dónde copiar</EmptyTitle>
              <EmptyDescription>
                {/* La frase distingue los dos vacíos: «no hay candidatos» y «los hay pero están
                    vacíos» se arreglan en lugares distintos. */}
                {candidates.length === 0
                  ? 'Este nivel no tiene otros niveles por encima ni subtipos hermanos.'
                  : 'Ninguno de los niveles relacionados tiene fotos en su galería todavía.'}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="flex max-h-[22rem] flex-col gap-3 overflow-y-auto">
            <RadioGroup
              value={selected ?? ''}
              onValueChange={setSelected}
              className="flex flex-col gap-3"
            >
              {groups.map(({ relation, rows }) => {
                return (
                  <div key={relation} className="flex flex-col gap-1.5">
                    <span className="flex items-center gap-1.5 font-medium text-muted-foreground text-xs">
                      <Layers className="h-3 w-3" aria-hidden />
                      {GROUP_LABEL[relation]}
                    </span>
                    {/* ☠️ La frase que evita el malentendido caro: copiar de la cadena NO es lo
                        que hace que las fotos se vean — eso ya pasaba por herencia. Es tomarlas
                        como propias para poder reacomodarlas sin afectar a los demás. */}
                    {relation === 'chain' ? (
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        Estas fotos ya se ven aquí por herencia. Copiarlas las vuelve propias de
                        este nivel, para reordenarlas o quitar alguna sin tocar el origen.
                      </p>
                    ) : null}
                    {rows.map((source) => (
                      <SourceOption key={`${source.ownerType}:${source.ownerId}`} source={source} />
                    ))}
                  </div>
                )
              })}
            </RadioGroup>
          </div>
        )}

        {usable ? (
          <fieldset className="flex flex-col gap-1.5 border-t border-border pt-3">
            <legend className="sr-only">Qué hacer con las fotos que ya están</legend>
            <RadioGroup
              value={mode}
              onValueChange={(value) => {
                setMode(value as CopyGalleryMode)
              }}
              className="flex flex-col gap-1.5"
            >
              <Label
                htmlFor={`${modeName}-append`}
                className="flex cursor-pointer items-start gap-2 text-xs font-normal"
              >
                <RadioGroupItem id={`${modeName}-append`} value="append" className="mt-0.5" />
                <span className="flex flex-col">
                  <span className="font-medium">Agregar al final</span>
                  <span className="text-[11px] text-muted-foreground">
                    Conserva las fotos que ya tiene y suma las nuevas detrás.
                  </span>
                </span>
              </Label>

              <Label
                htmlFor={`${modeName}-replace`}
                className="flex cursor-pointer items-start gap-2 text-xs font-normal"
              >
                <RadioGroupItem id={`${modeName}-replace`} value="replace" className="mt-0.5" />
                <span className="flex flex-col">
                  <span className="font-medium">Reemplazar</span>
                  <span className="text-[11px] text-muted-foreground">
                    {/* «Salen», no «se borran»: el archivo queda en el cartapacio. La palabra
                        importa — «se borran» manda a buscar cómo recuperarlas. */}
                    {currentPhotoCount > 0
                      ? `Las ${currentPhotoCount === 1 ? 'foto' : `${currentPhotoCount} fotos`} que ya están salen de la galería. Los archivos se conservan en el cartapacio.`
                      : 'La galería está vacía, así que hace lo mismo que agregar.'}
                  </span>
                </span>
              </Label>
            </RadioGroup>
          </fieldset>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setOpen(false)
            }}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!selected || copy.isPending}
            onClick={handleCopy}
            className="gap-1.5"
          >
            {copy.isPending ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            Copiar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
